import { Fragment, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Send, CalendarClock, Loader2, Globe, Facebook } from "lucide-react";
import { getHorse, getPreview, publishListing, scheduleListing, unpublishListing } from "@/lib/api";
import { errMsg } from "@/lib/applyApi";
import { fmtD, adChip, contractChip } from "@/lib/pipelineApi";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PreviewAdDialog } from "@/components/adtemplate/PreviewAdDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { StatusChip } from "./PipelineCard";
import { BrandName, brandName, closeShort } from "@/lib/brand";
import { fmtMT, todayCompany } from "@/lib/mountainTime";
import { StageActions } from "./StageActions";

const APPROVAL = { submitted: "Awaiting manager approval", changes_requested: "Changes requested from seller", approved: "Approved", scheduled: "Approved", published: "Approved", unpublished: "Approved" };
const todayMT = () => todayCompany();

function DateRows({ c }) {
  const d = c.dates || {};
  const rows = [
    ["Scheduled Facebook Post Date", d.facebook_post_date ? `${fmtD(d.facebook_post_date)} (${d.facebook_post_date_auto === false ? "Manual" : "Automatic"})` : "Not set", "fb-date"],
    ["Bidding Starts", d.bidding_starts ? `${fmtD(d.bidding_starts)}${d.bidding_starts_time ? ` · ${d.bidding_starts_time}` : ""}` : "Not set", "bidding-start"],
    ["Bidding Ends", d.close_date ? `${fmtD(d.close_date)} · ${d.close_time || closeShort()}` : "Not set", "bidding-close"],
    ["Website Publish Date", d.listing_date ? fmtD(d.listing_date) : "Not set", "publish-date"],
  ];
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
      {rows.map(([k, v, t]) => <Fragment key={t}><dt className="text-stone-500">{k}</dt><dd data-testid={`ad-publish-${t}`} className="font-semibold text-stone-900">{v}</dd></Fragment>)}
    </dl>
  );
}

function PublishDialog({ c, open, onOpenChange, onDone }) {
  const [busy, setBusy] = useState(null);
  const ld = c.dates?.listing_date;
  const canSchedule = (c.listing_status === "approved" || c.listing_status === "unpublished") && !!ld && ld > todayMT();
  const go = async (kind) => {
    setBusy(kind);
    try {
      const r = kind === "now" ? await publishListing(c.ids.horse_id, true) : await scheduleListing(c.ids.horse_id);
      toast.success(r?.listing_status === "scheduled" ? `Scheduled — goes live ${fmtD(ld)}` : "Published");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "Could not publish"));
    } finally {
      setBusy(null);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ad-publish-dialog" className="w-[calc(100%-1.5rem)] max-w-md rounded-xl">
        <DialogHeader>
          <DialogTitle>Publish {c.name}?</DialogTitle>
          <DialogDescription>Uses the existing publishing workflow. {c.is_test ? "TEST ad: it appears only in the TEST website feed, never on the LIVE website." : `The ad is added to the ${brandName()} website feed.`}</DialogDescription>
        </DialogHeader>
        <DateRows c={c} />
        <p className="text-xs text-stone-500 flex items-center gap-1.5"><Facebook className="h-3.5 w-3.5" />Facebook posting stays manual — use the Scheduled Facebook Post Date as your reminder and track it in the Ad Builder Publishing tab.</p>
        {!canSchedule && <p data-testid="ad-publish-schedule-note" className="text-xs text-amber-800">{c.listing_status === "scheduled" ? "Already scheduled — Publish Now makes it live immediately." : ld ? "The Website Publish Date is today or past — use Publish Now." : "Set a Website Publish Date in Sale Info → Sale Scheduling to schedule."}</p>}
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button data-testid="ad-publish-now" disabled={!!busy} onClick={() => go("now")} className="h-11 rounded-full w-full">{busy === "now" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" />Publish Now</>}</Button>
          <Button data-testid="ad-publish-schedule" variant="outline" disabled={!!busy || !canSchedule} onClick={() => go("schedule")} className="h-11 rounded-full w-full">
            {busy === "schedule" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CalendarClock className="h-4 w-4 mr-1.5" />Schedule for {ld ? fmtD(ld) : "Website Publish Date"}</>}
          </Button>
          <Button data-testid="ad-publish-cancel" variant="ghost" onClick={() => onOpenChange(false)} className="h-10 rounded-full w-full">Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Publishing section for Horse Workspace > Ad Details. All actions call the existing /horses/{id}/approve|schedule|publish endpoints.
export function AdPublishPanel({ c, onDone, onLeave, adDirty }) {
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const ls = c.listing_status;
  const eligible = ls === "approved" || ls === "scheduled" || ls === "unpublished";
  const [busyUnpub, setBusyUnpub] = useState(false);
  const unpublish = async () => {
    setBusyUnpub(true);
    try {
      await unpublishListing(c.ids.horse_id);
      toast.success("Ad unpublished — moved back to Ready to Post");
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "Could not unpublish"));
    } finally {
      setBusyUnpub(false);
    }
  };
  const openPreview = async () => {
    // Same preview as Ad Details: both ad versions (text first, Copy Text) then the selected photos/videos.
    try {
      const [p, h] = await Promise.all([getPreview(c.ids.horse_id), getHorse(c.ids.horse_id)]);
      setPreview({ p, media: h.media || [] });
    } catch { toast.error("Could not load preview"); }
  };
  return (
    <section data-testid="ad-publish-panel" className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
      <h3 className="font-semibold text-stone-900 flex items-center gap-2"><Globe className="h-4 w-4 text-stone-600" />Publishing</h3>
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusChip chip={adChip(c) || ["Ad Not Started", "stone"]} testId="ad-publish-status" />
        {contractChip(c) && <StatusChip chip={contractChip(c)} testId="ad-publish-contract" />}
        <span data-testid="ad-publish-approval" className="text-xs text-stone-700">Approval: {APPROVAL[ls] || "Not submitted yet"}</span>
      </div>
      <DateRows c={c} />
      <div className="flex flex-wrap gap-2">
        <Button data-testid="ad-publish-preview" variant="outline" onClick={openPreview} className="h-10 rounded-full"><Eye className="h-4 w-4 mr-1.5" />Preview Ad</Button>
        {eligible && (
          <Button data-testid="ad-publish-open" onClick={() => (adDirty ? toast.error("Save your ad changes first.") : setConfirm(true))} className="h-10 rounded-full">
            <Send className="h-4 w-4 mr-1.5" />{ls === "scheduled" ? "Publish Now…" : ls === "unpublished" ? "Republish Ad" : "Publish Ad"}
          </Button>
        )}
        {ls === "published" && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="ad-unpublish-open" variant="outline" disabled={busyUnpub || adDirty} className="h-10 rounded-full text-red-700 border-red-200 hover:bg-red-50">
                {busyUnpub ? <Loader2 className="h-4 w-4 animate-spin" /> : <><EyeOff className="h-4 w-4 mr-1.5" />Unpublish Ad</>}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="ad-unpublish-dialog" className="w-[calc(100%-1.5rem)] max-w-md rounded-xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Unpublish {c.name}?</AlertDialogTitle>
                <AlertDialogDescription>Unpublish this horse from the <BrandName /> website? The advertisement will be removed from public listings, but all information will be saved.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="ad-unpublish-cancel">Keep Published</AlertDialogCancel>
                <AlertDialogAction data-testid="ad-unpublish-confirm" onClick={unpublish} className="bg-red-700 hover:bg-red-800">Unpublish</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      {ls === "scheduled" && <p data-testid="ad-publish-scheduled-note" className="text-xs text-violet-800">Scheduled — stays in Ready to Post until it goes live on {fmtD(c.dates?.listing_date) || "its Ad Post Date"}.</p>}
      {ls === "published" && <p className="text-xs text-emerald-800">Published{c.is_test ? " to the TEST feed only" : ""}.</p>}
      {ls === "unpublished" && <p data-testid="ad-unpublished-note" className="text-xs text-stone-700">Unpublished{c.unpublished_by ? ` by ${c.unpublished_by}` : ""}{c.unpublished_at ? ` on ${fmtMT(c.unpublished_at)}` : ""} — removed from the website; everything is saved. Edit and republish any time.</p>}
      <StageActions c={c} onDone={onDone} onLeave={onLeave} inAdBuilder adDirty={adDirty} />
      <PublishDialog c={c} open={confirm} onOpenChange={setConfirm} onDone={onDone} />
      {/* Always mounted (open toggles) so the history-push effect doesn't double-run on mount under StrictMode and close itself. */}
      <PreviewAdDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)} horseId={c.ids.horse_id} preview={preview?.p} media={preview?.media} textFirst />
    </section>
  );
}
